<template>
  <div class="miniapps-page full-container">
    <div class="search-bar page-toolbar">
      <div class="flex-wrap">
        <Input
          v-model="searchText"
          placeholder="搜索应用名称"
          clearable
          class="search-input"
          @on-enter="handleSearch"
        />
        <Button type="primary" icon="md-search" @click="handleSearch">搜索</Button>
        <Button type="success" icon="md-add" @click="handleAdd">新建</Button>
      </div>
    </div>

    <div class="table-wrap">
      <BaseTable
        ref="table"
        full
        :columns="columns"
        :data="filteredData"
        :loading="loading"
        border
        @event="handleTableEvent"
      />
    </div>

    <!-- 新建/修改（基本信息）对话框 -->
    <Modal
      v-model="metaModalVisible"
      :title="metaModalTitle"
      width="560"
      @on-ok="handleMetaSubmit"
      :loading="metaConfirmLoading"
      @on-cancel="metaModalVisible = false"
    >
      <Form ref="metaFormRef" :model="metaFormData" :rules="metaRules" :label-width="100">
        <FormItem label="应用名称" prop="name">
          <Input v-model="metaFormData.name" placeholder="请输入应用名称" />
        </FormItem>
        <FormItem label="描述" prop="description">
          <Input v-model="metaFormData.description" type="textarea" :rows="3" placeholder="请输入描述" />
        </FormItem>
        <FormItem label="图标" prop="icon">
          <div class="icon-clip-wrap">
            <ImageClip
              v-model="metaFormData.icon"
              :editable="true"
              :option="iconClipOption"
            />
            <span class="icon-hint">裁剪为 200×200 使用</span>
          </div>
        </FormItem>
        <FormItem label="需登录" prop="private">
          <i-switch v-model="metaFormData.private" :true-value="1" :false-value="0">
            <span slot="open">是</span>
            <span slot="close">否</span>
          </i-switch>
          <span class="form-hint">private=1 需登录，private=0 无需登录</span>
        </FormItem>
        <FormItem label="状态" prop="disabled" v-if="metaEditId">
          <i-switch v-model="metaFormData.disabled" :true-value="1" :false-value="0">
            <span slot="open">禁用</span>
            <span slot="close">启用</span>
          </i-switch>
        </FormItem>
      </Form>
    </Modal>

    <!-- 应用编辑：左侧预览/代码，右侧 AI 开发 -->
    <Modal
      v-model="editorModalVisible"
      :title="editorTitle"
      width="1600"
      class-name="miniapp-editor-modal"
      :footer-hide="true"
      :mask-closable="false"
      @on-cancel="requestCloseEditor"
    >
      <div class="miniapp-editor-layout">
        <div class="editor-left">
          <div class="editor-left-toolbar">
            <div class="editor-left-toolbar-left">
              <RadioGroup v-model="viewMode" type="button" size="small">
                <Radio label="preview">预览</Radio>
                <Radio label="code">代码</Radio>
              </RadioGroup>
              <span v-if="lastCodeAppliedAt" class="code-applied-tip">已从 AI 更新代码</span>
            </div>
            <div class="editor-left-toolbar-right">
              <Button size="small" icon="md-refresh" :disabled="!editorContent" @click="updatePreviewUrl">刷新预览</Button>
              <Button type="primary" size="small" icon="md-checkmark" :loading="contentSaving" @click="saveContent">保存</Button>
            </div>
          </div>
          <div class="preview-size-bar" v-if="viewMode === 'preview'">
            <span class="preview-size-label">尺寸</span>
            <Button
              v-for="(sz, idx) in previewSizes"
              :key="idx"
              :type="previewSizeIndex === idx ? 'primary' : 'default'"
              size="small"
              class="size-chip"
              @click="previewSizeIndex = idx"
            >{{ sz.label || `${sz.w}×${sz.h}` }}</Button>
          </div>
          <div class="editor-left-body">
            <template v-if="viewMode === 'preview'">
              <div class="preview-viewport" :style="previewViewportStyle">
                <div class="preview-iframe-wrap" v-if="previewUrl">
                  <iframe
                    :src="previewUrl"
                    class="preview-iframe"
                    :style="previewIframeStyle"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    title="预览"
                  />
                </div>
                <div v-else class="preview-empty">暂无内容可预览，可用右侧 AI 生成完整 HTML 文件</div>
              </div>
            </template>
            <Input
              v-show="viewMode === 'code'"
              v-model="editorContent"
              type="textarea"
              :rows="20"
              :maxlength="codeTextareaMaxLength"
              placeholder="在此编辑完整单文件 HTML（含 style/script）"
              class="code-textarea"
            />
          </div>
        </div>

        <div class="editor-right">
          <div class="editor-right-top chat-panel">
            <div class="chat-panel-header">
              <div class="chat-panel-title-row">
                <span class="chat-panel-title">AI 开发</span>
                <span class="chat-panel-sub">DeepSeek · 完整 HTML</span>
              </div>
              <div class="chat-panel-actions">
                <Button
                  size="small"
                  class="chat-clear-btn"
                  :disabled="!chatMessages.length || chatSending"
                  @click="clearChat"
                >清空</Button>
              </div>
            </div>

            <!-- 基础配置：选中提示词展示 + 齿轮 + 保存 -->
            <div class="prompt-config-bar">
              <div class="prompt-active" :title="activePromptPreview">
                <Icon type="ios-document-outline" />
                <span class="prompt-active-label">{{ activePromptName || '未选择提示词' }}</span>
              </div>
              <div class="prompt-config-actions">
                <Poptip
                  v-model="promptConfigVisible"
                  placement="bottom-end"
                  transfer
                  width="420"
                  popper-class="miniapp-prompt-config-pop"
                >
                  <Button
                    size="small"
                    icon="md-settings"
                    class="prompt-icon-btn"
                    :disabled="chatSending"
                    title="基础配置 / 提示词"
                  ></Button>
                  <div slot="content" class="prompt-config-panel">
                    <div class="prompt-config-title">基础配置 · 提示词</div>
                    <div v-for="group in presetPromptGroups" :key="group.key" class="preset-group">
                      <span class="preset-group-label">{{ group.label }}</span>
                      <div class="preset-chips">
                        <button
                          v-for="item in group.items"
                          :key="item.name"
                          type="button"
                          class="preset-chip"
                          :class="{ active: activePromptName === item.name && activePromptSource === 'preset' }"
                          :disabled="chatSending"
                          @click="applyPresetPrompt(item)"
                        >{{ item.name }}</button>
                      </div>
                    </div>
                    <div class="prompt-config-divider">自定义提示词</div>
                    <div class="preset-chips custom-prompt-chips" v-if="customPrompts.length">
                      <button
                        v-for="item in customPrompts"
                        :key="'c-' + item.id"
                        type="button"
                        class="preset-chip"
                        :class="{ active: activePromptName === item.name && activePromptSource === 'custom' }"
                        :disabled="chatSending"
                        @click="applyCustomPrompt(item)"
                      >{{ item.name }}</button>
                    </div>
                    <div v-else class="prompt-config-empty">暂无自定义提示词，可点「+」保存当前输入</div>
                    <div class="preset-saved">
                      <AiPromptPanel
                        ref="promptPanel"
                        v-model="savedPromptDraft"
                        mod="miniapp-dev"
                        :textarea-rows="2"
                        :hide-mod-hint="true"
                        placeholder="从提示词库选择"
                        @select="onSavedPromptSelect"
                      />
                    </div>
                  </div>
                </Poptip>
                <Button
                  size="small"
                  icon="md-add"
                  class="prompt-icon-btn"
                  :disabled="chatSending || !(chatInput || '').trim()"
                  title="保存当前输入为提示词"
                  @click="openSavePromptModal"
                ></Button>
              </div>
            </div>

            <div
              class="chat-messages-wrap"
              ref="chatMessagesWrap"
              @scroll.passive="onChatMessagesScroll"
            >
              <div v-if="!chatMessages.length" class="chat-empty-hint">
                描述页面需求，或从上方「配置」选择提示词。新建请输出完整 HTML；局部修改可用行级补丁。
              </div>
              <div
                v-for="(msg, idx) in chatMessages"
                :key="idx"
                :class="['dev-chat-msg', msg.role === 'user' ? 'msg-user' : 'msg-bot']"
              >
                <div class="msg-bubble" :class="msg.role">
                  <template v-if="msg.role === 'user'">
                    <div v-if="msg.images && msg.images.length" class="msg-user-images">
                      <img
                        v-for="(img, ii) in msg.images"
                        :key="ii"
                        :src="img.url || img"
                        :alt="img.name || '图片'"
                        class="msg-user-image"
                      />
                    </div>
                    <div v-if="msg.content" class="msg-user-text">{{ msg.content }}</div>
                  </template>
                  <template v-else>
                    <div class="msg-markdown-wrap" :class="{ 'msg-streaming': msg.streaming }">
                      <div
                        v-if="msg.thinking && msg.thinking.trim()"
                        class="msg-thinking"
                        :class="{ 'is-collapsed': msg.thinkingCollapsed && !msg.streaming }"
                      >
                        <div class="msg-thinking-label" @click="toggleThinking(msg)">
                          <Icon type="ios-bulb-outline" />
                          <span>思考过程</span>
                          <span v-if="msg.streaming && !msg.thinkingCollapsed" class="msg-thinking-live">生成中…</span>
                          <Icon
                            class="msg-thinking-caret"
                            :type="msg.thinkingCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'"
                          />
                        </div>
                        <div
                          v-show="!msg.thinkingCollapsed"
                          class="msg-thinking-body"
                          :ref="msg.streaming ? 'streamingThinkingBody' : null"
                        >
                          <pre>{{ msg.thinking }}</pre>
                        </div>
                      </div>
                      <div v-if="msg.codeApplied" class="msg-code-applied">
                        <Icon type="md-code-working" />
                        {{ msg.applyMode === 'patch' ? '已按补丁更新左侧代码' : 'HTML 已提取并更新到左侧' }}
                        <a href="javascript:;" @click="reapplyExtractedCode(msg)">重新应用</a>
                      </div>
                      <div v-if="msg.extractFailed && !msg.streaming" class="msg-extract-failed">
                        <Icon type="md-warning" />
                        <span>{{ msg.extractError || '未能提取可应用的代码（完整 HTML 或行级补丁）' }}</span>
                        <Button size="small" type="primary" ghost :disabled="chatSending" @click="regenerateFromMessage(msg)">重新生成</Button>
                      </div>
                      <div class="msg-answer markdown-body" v-if="displayAssistantContent(msg)">
                        <BaseMarkdownView :value="displayAssistantContent(msg)" />
                      </div>
                      <span v-else-if="msg.streaming && !msg.thinking" class="cursor-blink">...</span>
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="chatSending && !streamingMessage" class="dev-chat-msg msg-bot">
                <div class="msg-bubble bot typing">正在回复...</div>
              </div>
            </div>

            <div class="chat-composer" @paste="onChatPaste">
              <div v-if="chatImages.length" class="chat-images-bar">
                <div v-for="(img, i) in chatImages" :key="i" class="chat-image-thumb">
                  <img :src="img.preview || img.url" :alt="img.name || '图片'" />
                  <Icon type="md-close" class="chat-image-remove" @click.native="removeChatImage(i)" />
                </div>
              </div>
              <Input
                v-model="chatInput"
                type="textarea"
                :rows="2"
                placeholder="描述页面或修改；可附图 / Ctrl+V 粘贴图片。Enter 发送，Shift+Enter 换行"
                @on-keydown="onChatKeydown"
                class="chat-input-area"
              />
              <div class="chat-input-actions">
                <div class="chat-input-actions-left">
                  <input
                    ref="chatImageInput"
                    type="file"
                    accept="image/*"
                    multiple
                    style="display: none;"
                    @change="onChatImageChange"
                  />
                  <Button size="small" icon="md-image" :disabled="chatSending" @click="triggerChatImageInput">图片</Button>
                  <Poptip
                    v-model="skillsPopVisible"
                    placement="top-start"
                    transfer
                    width="320"
                    popper-class="miniapp-skills-pop"
                  >
                    <Button size="small" icon="md-flash" :disabled="chatSending || skillsLoading">
                      技能{{ enabledSkillsCount ? ` (${enabledSkillsCount})` : '' }}
                    </Button>
                    <div slot="content" class="skills-pop-panel">
                      <div class="skills-pop-title">勾选技能（并入系统提示）</div>
                      <div v-if="skillsList.length === 0 && !skillsLoading" class="panel-placeholder">暂无技能</div>
                      <Spin v-else-if="skillsLoading" fix />
                      <div v-else class="skills-check-list">
                        <Checkbox
                          v-for="(sk, idx) in skillsList"
                          :key="sk.id || idx"
                          v-model="sk.enabled"
                          class="skill-check-item"
                        >
                          <span class="skill-name">{{ sk.name }}</span>
                          <span v-if="sk.description" class="skill-desc">{{ sk.description }}</span>
                        </Checkbox>
                      </div>
                    </div>
                  </Poptip>
                </div>
                <div class="chat-input-actions-right">
                  <Button
                    v-if="chatSending"
                    size="small"
                    icon="md-square"
                    class="chat-stop-btn"
                    @click="stopChatStream"
                  >停止</Button>
                  <Button
                    v-else
                    type="primary"
                    size="small"
                    class="chat-send-btn"
                    @click="sendChatMessage"
                  >发送</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <Modal v-model="savePromptModalVisible" title="保存提示词" :mask-closable="false" footer-hide>
      <Form :label-width="80">
        <FormItem label="标题">
          <Input v-model="savePromptForm.name" placeholder="为提示词命名" />
        </FormItem>
        <FormItem label="内容">
          <Input v-model="savePromptForm.content" type="textarea" :rows="6" placeholder="提示词正文" />
        </FormItem>
        <div class="flex-wrap flex-center split5">
          <Button type="primary" :loading="savePromptLoading" @click="submitSavePrompt">提交</Button>
          <Button @click="savePromptModalVisible = false">取消</Button>
        </div>
      </Form>
    </Modal>
  </div>
</template>

<script>
import ImageClip from '@/components/form/ImageClip';
import BaseMarkdownView from '@/components/base/MarkdownView';
import AiPromptPanel from '@/components/ai/AiPromptPanel.vue';
import {
  extractPureHtmlDocument,
  extractHtmlSpan,
  isCompleteHtmlDocument,
} from '@/components/ai/is-complete-html';
import { readAssistantChatStream } from '@/components/ai/read-assistant-chat-stream';

const API_BASE = '/ai/miniapps';
const SKILLS_API = '/ai/skills';
const CHAT_API = '/ai/chat';
const PROMPT_MOD = 'miniapp-dev';
const DEEPSEEK_MODEL = 'deepseek-v4';

const BASE_SYSTEM_PROMPT = `你是「单文件 HTML 应用」开发助手。本模块只开发【一个可独立运行的完整 .html 文件】，不是多文件工程、不是框架脚手架。

【硬性约束】
1. 所有内容必须在同一个 HTML 文件内（可用 <style> / <script>）。禁止多文件方案与 Vue/React/Webpack/Vite 脚手架。
2. 第三方库必须用国内 CDN（bootcdn.cn、staticfile.org、cdn.bytedance.com、unpkg.zhimg.com、npmmirror.com），禁止 jsdelivr/cdnjs/unpkg.com。
3. 界面简洁清晰，优先原生 HTML/CSS/JS；思考过程用中文。
4. 代码块外可用中文简要说明；不要用省略号漏掉关键代码。

【输出格式二选一】
A. 整页重写（新建、大改、结构变更时）：输出完整 HTML，放在 \`\`\`html 代码块中（含 <!DOCTYPE html> 与完整 <html>…</html>）。
B. 局部修改（小改、修 bug、改样式/文案时）：优先输出行级补丁，放在 \`\`\`miniapp-edit 代码块中，JSON 结构如下：
\`\`\`miniapp-edit
{
  "mode": "patch",
  "summary": "一句话说明改了什么",
  "ops": [
    { "op": "replace_lines", "start": 12, "end": 20, "content": "替换后的多行文本" },
    { "op": "insert_after", "line": 30, "content": "在第30行后插入的文本" },
    { "op": "delete_lines", "start": 40, "end": 45 },
    { "op": "replace_text", "find": "旧片段", "replace": "新片段", "all": false }
  ]
}
\`\`\`
行号以我提供的「带行号源码」为准（1 起算，含首尾）。多个 ops 按数组顺序依次应用。
也可使用轻量格式 \`\`\`miniapp-lines ：
<<<REPLACE 12-20
替换后的多行
>>>
<<<INSERT_AFTER 30
插入内容
>>>
<<<DELETE 40-45
>>>
小改优先 patch；无法准确定位行号或改动面大时再用完整 HTML 重写。`;

const PRESET_PROMPT_GROUPS = [
  {
    key: 'create',
    label: '新建',
    items: [
      {
        name: '空白工具页',
        prompt: '请从零创建一个简洁的单文件 HTML 工具页：顶部标题栏、主内容区、底部说明。使用原生 CSS，无需框架。直接输出完整 HTML 文件。',
      },
      {
        name: '数据仪表盘',
        prompt: '请创建一个单文件 HTML 数据仪表盘：含若干统计卡片与一个图表（ECharts 请用国内 CDN）。界面现代简洁，直接输出完整 HTML 文件。',
      },
      {
        name: '表单页',
        prompt: '请创建一个单文件 HTML 表单页：常见字段校验、提交后本地展示结果（可不接后端）。直接输出完整 HTML 文件。',
      },
      {
        name: '列表+详情',
        prompt: '请创建一个单文件 HTML 页面：左侧列表、右侧详情，点击列表项切换详情。纯前端假数据即可。直接输出完整 HTML 文件。',
      },
    ],
  },
  {
    key: 'polish',
    label: '优化',
    items: [
      {
        name: '美化样式',
        prompt: '请基于当前左侧 HTML，优化视觉与排版（配色、间距、圆角、阴影），保持功能不变。改动面不大请用 miniapp-edit 行级补丁；大改再输出完整 HTML。',
      },
      {
        name: '移动端适配',
        prompt: '请基于当前左侧 HTML，做好移动端响应式适配（375 宽度可用）。优先用 miniapp-edit 行级补丁；结构大改再输出完整 HTML。',
      },
      {
        name: '修复问题',
        prompt: '请检查当前左侧 HTML 的明显 bug/兼容性问题并修复。优先用 miniapp-edit 行级补丁精确替换相关行；无法定位时再输出完整 HTML。',
      },
    ],
  },
  {
    key: 'feature',
    label: '增强',
    items: [
      {
        name: '加本地存储',
        prompt: '请基于当前左侧 HTML，增加 localStorage 持久化关键数据的能力，并给出清空入口。优先行级补丁；改动较大再输出完整 HTML。',
      },
      {
        name: '加图表',
        prompt: '请基于当前左侧 HTML，增加合适的图表展示（ECharts 国内 CDN），数据可用示例数据。优先行级补丁；改动较大再输出完整 HTML。',
      },
      {
        name: '按图还原',
        prompt: '请根据我上传的设计/截图，用单文件 HTML 尽量还原界面（布局、配色、主要交互）。直接输出完整 HTML 文件。',
      },
    ],
  },
];

function normalizePrivate(v) {
  return v === true || v === 1 || v === '1' ? 1 : 0;
}

function needsLogin(v) {
  return normalizePrivate(v) === 1;
}

function extractStreamParts(data) {
  let content = '';
  let reasoning = '';
  if (data == null) return { content, reasoning };
  if (typeof data === 'string') return { content: data, reasoning };

  const choice = data.choices && data.choices[0];
  const delta = (choice && choice.delta) || data.delta || {};
  const message = (choice && choice.message) || data.message || {};

  if (typeof delta.content === 'string') content = delta.content;
  else if (typeof data.content === 'string') content = data.content;
  else if (typeof data.text === 'string') content = data.text;
  else if (typeof message.content === 'string') content = message.content;
  else if (data.data) {
    if (typeof data.data === 'string') content = data.data;
    else if (typeof data.data.content === 'string') content = data.data.content;
  }

  if (typeof delta.reasoning_content === 'string') reasoning = delta.reasoning_content;
  else if (typeof data.reasoning_content === 'string') reasoning = data.reasoning_content;
  else if (typeof data.thinking === 'string') reasoning = data.thinking;
  else if (typeof data.thinking_content === 'string') reasoning = data.thinking_content;
  else if (typeof message.reasoning_content === 'string') reasoning = message.reasoning_content;
  else if (delta.reasoning && typeof delta.reasoning === 'string') reasoning = delta.reasoning;

  return { content, reasoning };
}

function extractHtmlFromAssistant(text) {
  const extracted = extractPureHtmlDocument(text);
  if (isCompleteHtmlDocument(extracted)) return extracted;
  // 兜底：即使前后有说明，也按最后一个 </html> 截取
  const span = extractHtmlSpan(text);
  if (span) return span;
  return '';
}

/** 发给模型的源码上下文上限（字符）；过小会导致 AI 基于残缺源码重写并写回截断结果 */
const SOURCE_CONTEXT_MAX_CHARS = 500000;

function withLineNumbers(source, maxChars = SOURCE_CONTEXT_MAX_CHARS) {
  const lines = String(source || '').split('\n');
  let out = '';
  for (let i = 0; i < lines.length; i += 1) {
    const row = `${i + 1}| ${lines[i]}\n`;
    if (out.length + row.length > maxChars) {
      out += `...（已截断，共 ${lines.length} 行；请勿基于残缺内容输出不完整页面，大改请要求用户分段或仅用 replace_text 补丁）\n`;
      break;
    }
    out += row;
  }
  return out;
}

function buildEditorSourceMessage(source) {
  const raw = String(source || '').trim();
  if (!raw) {
    return '【当前左侧编辑器为空】请从零生成完整可运行的单文件 HTML（```html 代码块）。';
  }
  const numbered = withLineNumbers(raw);
  const truncated = numbered.indexOf('...（已截断') !== -1;
  if (!truncated) {
    return `【当前左侧源码（带行号，局部修改请引用行号；大改可输出完整 HTML）】\n\`\`\`text\n${numbered}\`\`\``;
  }
  // 带行号过大时改为原文截取，并明确禁止输出残缺整页
  const clipped = raw.length > SOURCE_CONTEXT_MAX_CHARS
    ? `${raw.slice(0, SOURCE_CONTEXT_MAX_CHARS)}\n<!-- 源码过长已截断 ${raw.length} 字符 -->`
    : raw;
  return `【当前左侧源码过长，以下为截取片段（原文 ${raw.length} 字符）。禁止输出残缺 HTML；请优先用 miniapp-edit 的 replace_text / 可见行号补丁；只有确认能给出完整可运行页面时才整页重写。】\n\`\`\`html\n${clipped}\n\`\`\``;
}

function extractFenceBlocks(text, lang) {
  const blocks = [];
  if (!text) return blocks;
  const re = new RegExp('```' + lang + '\\s*([\\s\\S]*?)```', 'gi');
  let m;
  while ((m = re.exec(text)) !== null) {
    const body = String(m[1] || '').trim();
    if (body) blocks.push(body);
  }
  return blocks;
}

function parseMiniappEditJson(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(text.slice(start, end + 1));
      } catch (e2) {
        return null;
      }
    }
    return null;
  }
}

function parseMiniappLinesOps(raw) {
  const text = String(raw || '').trim();
  if (!text) return [];
  const ops = [];
  const re = /<<<\s*(REPLACE|INSERT_AFTER|DELETE)\s+([^\n>]+)\s*\n?([\s\S]*?)>>>/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const kind = String(m[1] || '').toUpperCase();
    const range = String(m[2] || '').trim();
    const content = String(m[3] || '').replace(/^\n/, '').replace(/\n$/, '');
    if (kind === 'REPLACE') {
      const parts = range.split('-').map(s => parseInt(String(s).trim(), 10));
      const start = parts[0];
      const end = parts.length > 1 ? parts[1] : start;
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
      ops.push({ op: 'replace_lines', start, end, content });
    } else if (kind === 'INSERT_AFTER') {
      const line = parseInt(range, 10);
      if (!Number.isFinite(line)) continue;
      ops.push({ op: 'insert_after', line, content });
    } else if (kind === 'DELETE') {
      const parts = range.split('-').map(s => parseInt(String(s).trim(), 10));
      const start = parts[0];
      const end = parts.length > 1 ? parts[1] : start;
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
      ops.push({ op: 'delete_lines', start, end });
    }
  }
  return ops;
}

function extractPatchFromAssistant(text) {
  const editBlocks = extractFenceBlocks(text, 'miniapp-edit');
  for (let i = 0; i < editBlocks.length; i += 1) {
    const parsed = parseMiniappEditJson(editBlocks[i]);
    if (!parsed) continue;
    if (parsed.mode === 'full' && parsed.html && isCompleteHtmlDocument(String(parsed.html).trim())) {
      return { mode: 'full', html: String(parsed.html).trim(), summary: parsed.summary || '' };
    }
    const ops = Array.isArray(parsed.ops) ? parsed.ops : [];
    if (ops.length) {
      return { mode: 'patch', ops, summary: parsed.summary || '' };
    }
    if (parsed.html && isCompleteHtmlDocument(String(parsed.html).trim())) {
      return { mode: 'full', html: String(parsed.html).trim(), summary: parsed.summary || '' };
    }
  }

  const lineBlocks = extractFenceBlocks(text, 'miniapp-lines');
  for (let i = 0; i < lineBlocks.length; i += 1) {
    const ops = parseMiniappLinesOps(lineBlocks[i]);
    if (ops.length) return { mode: 'patch', ops, summary: '' };
  }
  return null;
}

function applyLineOps(source, ops) {
  let lines = String(source == null ? '' : source).split('\n');
  const list = Array.isArray(ops) ? ops : [];
  for (let i = 0; i < list.length; i += 1) {
    const op = list[i] || {};
    const type = String(op.op || '').toLowerCase();
    if (type === 'replace_lines') {
      const start = Math.max(1, parseInt(op.start, 10) || 1);
      const end = Math.max(start, parseInt(op.end, 10) || start);
      const contentLines = String(op.content == null ? '' : op.content).split('\n');
      lines = lines.slice(0, start - 1).concat(contentLines, lines.slice(end));
    } else if (type === 'insert_after') {
      const line = Math.max(0, parseInt(op.line, 10) || 0);
      const contentLines = String(op.content == null ? '' : op.content).split('\n');
      const at = Math.min(line, lines.length);
      lines = lines.slice(0, at).concat(contentLines, lines.slice(at));
    } else if (type === 'delete_lines') {
      const start = Math.max(1, parseInt(op.start, 10) || 1);
      const end = Math.max(start, parseInt(op.end, 10) || start);
      lines = lines.slice(0, start - 1).concat(lines.slice(end));
    } else if (type === 'replace_text') {
      const find = String(op.find == null ? '' : op.find);
      if (!find) continue;
      const replace = String(op.replace == null ? '' : op.replace);
      let joined = lines.join('\n');
      if (op.all) {
        joined = joined.split(find).join(replace);
      } else {
        const idx = joined.indexOf(find);
        if (idx < 0) {
          throw new Error(`replace_text 未找到片段：${find.slice(0, 40)}`);
        }
        joined = joined.slice(0, idx) + replace + joined.slice(idx + find.length);
      }
      lines = joined.split('\n');
    } else {
      throw new Error(`不支持的补丁操作：${type || '(空)'}`);
    }
  }
  return lines.join('\n');
}

function applyAssistantCodeToSource(source, assistantText) {
  const patch = extractPatchFromAssistant(assistantText);
  if (patch && patch.mode === 'full' && patch.html) {
    return { ok: true, mode: 'full', html: patch.html, summary: patch.summary || '' };
  }
  if (patch && patch.mode === 'patch' && patch.ops && patch.ops.length) {
    try {
      const html = applyLineOps(source, patch.ops);
      return { ok: true, mode: 'patch', html, summary: patch.summary || '', opsCount: patch.ops.length };
    } catch (e) {
      return { ok: false, error: (e && e.message) || '补丁应用失败' };
    }
  }
  const full = extractHtmlFromAssistant(assistantText);
  if (full) {
    return { ok: true, mode: 'full', html: full, summary: '' };
  }
  return { ok: false, error: '未能提取完整 HTML 或行级补丁' };
}

function stripHtmlBlocksForDisplay(text) {
  if (!text) return '';
  let out = String(text)
    .replace(/```miniapp-edit\s*[\s\S]*?```/gi, '\n\n> （行级补丁已应用到左侧编辑器）\n\n')
    .replace(/```miniapp-lines\s*[\s\S]*?```/gi, '\n\n> （行级补丁已应用到左侧编辑器）\n\n')
    .replace(/```html\s*[\s\S]*?```/gi, '\n\n> （完整 HTML 已同步到左侧编辑器）\n\n')
    .replace(/```(?:[a-zA-Z0-9_-]*)?\s*([\s\S]*?)```/g, (full, code) => {
      if (isCompleteHtmlDocument(String(code || '').trim())) {
        return '\n\n> （完整 HTML 已同步到左侧编辑器）\n\n';
      }
      return full;
    });
  return out.trim();
}

function stripHtmlBlocksForHistory(text) {
  if (!text) return '';
  return String(text)
    .replace(/```miniapp-edit\s*[\s\S]*?```/gi, '（已输出行级补丁到编辑器）')
    .replace(/```miniapp-lines\s*[\s\S]*?```/gi, '（已输出行级补丁到编辑器）')
    .replace(/```html\s*[\s\S]*?```/gi, '（已输出完整 HTML 到编辑器）')
    .replace(/```(?:[a-zA-Z0-9_-]*)?\s*([\s\S]*?)```/g, (full, code) => {
      if (isCompleteHtmlDocument(String(code || '').trim())) return '（已输出完整 HTML 到编辑器）';
      return full;
    })
    .trim();
}

export default {
  components: { ImageClip, BaseMarkdownView, AiPromptPanel },
  data() {
    return {
      loading: false,
      items: [],
      searchText: '',

      metaModalVisible: false,
      metaModalTitle: '新建应用',
      metaConfirmLoading: false,
      metaEditId: null,
      metaFormData: {
        name: '',
        icon: '',
        content: '',
        description: '',
        private: 1,
        disabled: 0,
      },
      metaRules: {
        name: [{ required: true, message: '请输入应用名称', trigger: 'blur' }],
      },
      iconClipOption: {
        uploader: 'session/upload',
        w2h: 1,
      },

      editorModalVisible: false,
      editorEditId: null,
      editorTitle: '编辑应用',
      viewMode: 'preview',
      editorContent: '',
      editorSavedContent: '',
      editorSavedSkills: '',
      codeTextareaMaxLength: 2000000,
      contentSaving: false,
      previewSizes: [
        { w: 1920, h: 1280 },
        { w: 1024, h: 768 },
        { w: 900, h: 600 },
        { w: 375, h: 700, label: '375×700(移动端)' },
      ],
      previewSizeIndex: 0,
      previewDisplayScale: 0.45,
      lastCodeAppliedAt: 0,

      chatMessages: [],
      chatInput: '',
      chatImages: [],
      chatSending: false,
      streamingMessage: false,
      chatAborted: false,
      chatStickToBottom: true,
      _chatScrollIgnoreUntil: 0,
      savedPromptDraft: '',
      presetPromptGroups: PRESET_PROMPT_GROUPS,
      customPrompts: [],
      activePromptName: '',
      activePromptSource: '',
      activePromptPreview: '',
      promptConfigVisible: false,
      skillsPopVisible: false,
      skillsList: [],
      skillsLoading: false,

      savePromptModalVisible: false,
      savePromptLoading: false,
      savePromptForm: { name: '', content: '' },

      previewBlobUrl: null,

      columns: [
        { type: 'index', width: 60, title: '序号' },
        {
          title: '图标',
          key: 'icon',
          width: 80,
          align: 'center',
          render: (h, params) => {
            const icon = params.row.icon;
            if (!icon) return h('span', '-');
            return h('img', {
              attrs: { src: icon, alt: '' },
              style: { width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' },
            });
          },
        },
        {
          title: '应用名称',
          key: 'name',
          minWidth: 160,
          type: 'text',
          linkEvent: 'open',
        },
        {
          title: '需登录',
          key: 'private',
          width: 100,
          align: 'center',
          render: (h, params) => {
            const login = needsLogin(params.row.private);
            return h('span', {
              class: login ? 'login-tag login-tag--yes' : 'login-tag login-tag--no',
            }, login ? '需登录' : '无需登录');
          },
        },
        {
          title: '启用',
          key: 'disabled',
          width: 100,
          align: 'center',
          render: (h, params) => {
            const row = params.row;
            const isDisabled = row.disabled === 1;
            const canEdit = this.canEdit(row);
            if (!canEdit) {
              return h('span', { class: 'status-text' }, isDisabled ? '禁用' : '启用');
            }
            return h('i-switch', {
              props: {
                value: isDisabled ? 1 : 0,
                'true-value': 0,
                'false-value': 1,
                size: 'small',
              },
              on: {
                'on-change': (v) => this.toggleDisabled(row, v),
              },
            });
          },
        },
        {
          title: '查看次数',
          type: 'number',
          width: 100,
          key: 'count',
        },
        {
          title: '创建时间',
          key: 'created_at',
          width: 160,
          type: 'time',
        },
        {
          title: '创建人',
          type: 'user',
          key: 'created_by',
          width: 120,
        },
        {
          title: '编辑',
          key: 'edit',
          width: 80,
          align: 'center',
          render: (h, params) => {
            if (!this.canEdit(params.row)) return h('span', '-');
            return h('Button', {
              props: { size: 'small' },
              on: { click: () => this.handleTableEvent({ type: 'edit', data: params.row }) },
            }, '设计');
          },
        },
        {
          title: '操作',
          key: 'action',
          fixed: 'right',
          width: 140,
          render: (h, params) => {
            const row = params.row;
            if (!this.canEdit(row)) return h('span', { style: { color: 'var(--subtext2)' } }, '-');
            return h('div', [
              h('Button', {
                props: { size: 'small' },
                style: { marginRight: '8px' },
                on: { click: () => this.handleTableEvent({ type: 'modify', data: row }) },
              }, '修改'),
              h('Button', {
                props: { size: 'small' },
                on: { click: () => this.handleDelete(row) },
              }, '删除'),
            ]);
          },
        },
      ],
    };
  },
  computed: {
    session() {
      return this.$store.getters['session/session'] || {};
    },
    isAdmin() {
      return this.session.roles && this.session.roles.some(r => r.id === 'admin');
    },
    filteredData() {
      let result = [...this.items];
      if (this.searchText) {
        const q = this.searchText.toLowerCase();
        result = result.filter(item => (item.name && item.name.toLowerCase().includes(q)));
      }
      return result;
    },
    currentPreviewSize() {
      const list = this.previewSizes || [];
      const idx = this.previewSizeIndex;
      return list[idx] || list[0] || { w: 1920, h: 1280 };
    },
    previewViewportStyle() {
      const sz = this.currentPreviewSize;
      const scale = this.previewDisplayScale || 0.45;
      const w = Math.round((sz.w || 1920) * scale);
      const h = Math.round((sz.h || 1280) * scale);
      return {
        width: w + 'px',
        height: h + 'px',
      };
    },
    previewUrl() {
      return this.previewBlobUrl || '';
    },
    previewIframeStyle() {
      const sz = this.currentPreviewSize;
      const scale = this.previewDisplayScale || 0.45;
      return {
        width: (sz.w || 1920) + 'px',
        height: (sz.h || 1280) + 'px',
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
      };
    },
    enabledSkillsCount() {
      return (this.skillsList || []).filter(s => s.enabled).length;
    },
    systemPrompt() {
      let prompt = BASE_SYSTEM_PROMPT;
      const enabled = (this.skillsList || []).filter(s => s.enabled && (s.content || '').trim());
      if (enabled.length) {
        prompt += '\n\n以下技能说明请一并遵守：\n';
        enabled.forEach(s => { prompt += (s.content || '').trim() + '\n\n'; });
      }
      return prompt.trim();
    },
    editorHasUnsavedChanges() {
      if (!this.editorModalVisible && !this.editorEditId) return false;
      const contentDirty = String(this.editorContent || '') !== String(this.editorSavedContent || '');
      const skillsDirty = this.getEditorSkillsIds() !== String(this.editorSavedSkills || '');
      return contentDirty || skillsDirty;
    },
  },
  watch: {
    editorContent() {
      if (this.editorModalVisible && this.viewMode === 'preview') {
        if (this._previewUpdateTimer) clearTimeout(this._previewUpdateTimer);
        this._previewUpdateTimer = setTimeout(() => this.updatePreviewUrl(), 300);
      }
    },
    viewMode(mode) {
      if (mode === 'preview' && this.editorModalVisible) {
        this.updatePreviewUrl();
      }
    },
  },
  mounted() {
    this._onEditorBeforeUnload = (e) => {
      if (!this.editorModalVisible || !this.editorHasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', this._onEditorBeforeUnload);
    this.$api.afterLogin(() => {
      this.fetchData();
      this.fetchCustomPrompts();
    });
  },
  beforeDestroy() {
    if (this._onEditorBeforeUnload) {
      window.removeEventListener('beforeunload', this._onEditorBeforeUnload);
    }
    this.stopChatStream();
    this.revokePreviewUrl();
    this.revokeChatImagePreviews();
    if (this._previewUpdateTimer) clearTimeout(this._previewUpdateTimer);
  },
  methods: {
    canEdit(row) {
      if (!this.session || !this.session.id) return false;
      return this.isAdmin || row.created_by === this.session.id;
    },
    handleSearch() {},
    async fetchData() {
      this.loading = true;
      try {
        const res = await this.$api.get(API_BASE);
        this.items = (res.data && res.data.data) || res.data || [];
        if (!Array.isArray(this.items)) this.items = [];
      } catch (err) {
        this.$Message.error('获取应用列表失败');
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async fetchCustomPrompts() {
      try {
        const res = await this.$api.get('ai/parse', { params: { m: PROMPT_MOD } });
        const raw = (res.data && res.data.data) || res.data || [];
        this.customPrompts = Array.isArray(raw) ? raw : [];
      } catch (e) {
        this.customPrompts = [];
      }
    },
    handleAdd() {
      this.metaModalTitle = '新建应用';
      this.metaEditId = null;
      this.metaFormData = {
        name: '',
        icon: '',
        content: '',
        description: '',
        private: 1,
        disabled: 0,
      };
      this.metaModalVisible = true;
      this.$nextTick(() => this.$refs.metaFormRef && this.$refs.metaFormRef.resetFields());
    },
    handleTableEvent(e) {
      const { type, data: row } = e || {};
      if (type === 'open') {
        this.openAppInNewWindow(row);
      } else if (type === 'edit') {
        this.openEditor(row);
      } else if (type === 'modify') {
        this.openMetaEdit(row);
      } else if (type === 'delete') {
        this.handleDelete(row);
      }
    },
    getAppViewUrl(item) {
      const base = (this.$api.defaults.baseURL || '').replace(/\/$/, '');
      const id = item.id;
      if (needsLogin(item.private)) {
        const token = String(this.session.token || '').replace(/^Bearer\s+/i, '');
        return `${base}/s/${encodeURIComponent(id)}?authkey=${token}`;
      }
      return `${base}/s/${encodeURIComponent(id)}`;
    },
    openAppInNewWindow(row) {
      if (!row || !row.id) return;
      window.open(this.getAppViewUrl(row), '_blank');
    },
    openEditor(row) {
      this.editorEditId = row.id;
      this.editorTitle = `编辑：${row.name || '应用'}`;
      this.editorContent = row.content || '';
      this.editorSavedContent = this.editorContent;
      this.editorSavedSkills = row && row.skills != null ? String(row.skills) : '';
      this.viewMode = 'preview';
      this.chatMessages = [];
      this.chatInput = '';
      this.chatImages = [];
      this.savedPromptDraft = '';
      this.activePromptName = '';
      this.activePromptSource = '';
      this.activePromptPreview = '';
      this.lastCodeAppliedAt = 0;
      this.promptConfigVisible = false;
      this.skillsPopVisible = false;
      this.chatStickToBottom = true;
      this.editorModalVisible = true;
      this.fetchSkills(row && row.skills).then(() => {
        this.editorSavedSkills = this.getEditorSkillsIds();
      });
      this.fetchCustomPrompts();
      this.$nextTick(() => this.updatePreviewUrl());
    },
    markEditorSaved() {
      this.editorSavedContent = String(this.editorContent || '');
      this.editorSavedSkills = this.getEditorSkillsIds();
    },
    requestCloseEditor() {
      if (this._forceCloseEditor) {
        this.doCloseEditor();
        return;
      }
      if (!this.editorHasUnsavedChanges) {
        this.doCloseEditor();
        return;
      }
      // Modal 已因取消变为 false，先挡回再确认
      this.editorModalVisible = true;
      this.$Modal.confirm({
        title: '未保存的修改',
        content: '当前内容尚未保存，关闭后将丢失未保存的修改。确定关闭吗？',
        okText: '仍要关闭',
        cancelText: '继续编辑',
        onOk: () => {
          this._forceCloseEditor = true;
          this.doCloseEditor();
          this._forceCloseEditor = false;
        },
      });
    },
    doCloseEditor() {
      this.stopChatStream();
      this.revokePreviewUrl();
      this.revokeChatImagePreviews();
      this.editorModalVisible = false;
      this.editorEditId = null;
      this.editorContent = '';
      this.editorSavedContent = '';
      this.editorSavedSkills = '';
      this.chatMessages = [];
      this.chatImages = [];
      this.skillsList = [];
      this.lastCodeAppliedAt = 0;
    },
    closeEditor() {
      this.requestCloseEditor();
    },
    revokePreviewUrl() {
      if (this.previewBlobUrl) {
        URL.revokeObjectURL(this.previewBlobUrl);
        this.previewBlobUrl = null;
      }
    },
    revokeChatImagePreviews() {
      (this.chatImages || []).forEach((img) => {
        if (img && img.preview && String(img.preview).startsWith('blob:')) {
          try { URL.revokeObjectURL(img.preview); } catch (e) { /* ignore */ }
        }
      });
    },
    updatePreviewUrl() {
      this.revokePreviewUrl();
      const raw = (this.editorContent || '').trim();
      if (!raw) return;
      const blob = new Blob([raw], { type: 'text/html; charset=utf-8' });
      this.previewBlobUrl = URL.createObjectURL(blob);
    },
    getEditorSkillsIds() {
      return (this.skillsList || []).filter(s => s.enabled).map(s => s.id).join(',');
    },
    async fetchSkills(appSkills) {
      const skillIds = (appSkills != null && appSkills !== '' ? String(appSkills) : '')
        .split(',')
        .map(s => String(s).trim())
        .filter(Boolean);
      this.skillsLoading = true;
      try {
        const res = await this.$api.get(SKILLS_API);
        const raw = (res.data && res.data.data) || res.data || [];
        const list = Array.isArray(raw) ? raw : [];
        this.skillsList = list.map(s => ({
          ...s,
          id: s.id,
          name: s.name || '',
          description: s.description || '',
          content: s.content || '',
          enabled: skillIds.includes(String(s.id)),
        }));
      } catch (err) {
        this.$Message.error('获取技能列表失败');
        this.skillsList = [];
      } finally {
        this.skillsLoading = false;
      }
    },
    setActivePrompt(name, source, preview) {
      this.activePromptName = name || '';
      this.activePromptSource = source || '';
      this.activePromptPreview = preview || name || '';
    },
    applyPresetPrompt(item) {
      if (!item || !item.prompt) return;
      this.chatInput = item.prompt;
      this.setActivePrompt(item.name, 'preset', item.prompt);
      this.promptConfigVisible = false;
      this.$Message.info(`已填入「${item.name}」，可修改后发送`);
    },
    applyCustomPrompt(item) {
      const text = item && item.content != null ? String(item.content) : '';
      if (!text.trim()) return;
      this.chatInput = text;
      this.savedPromptDraft = text;
      this.setActivePrompt(item.name || '自定义提示词', 'custom', text);
      this.promptConfigVisible = false;
      this.$Message.info(`已填入提示词「${(item && item.name) || ''}」`);
    },
    onSavedPromptSelect(item) {
      const text = item && item.content != null ? String(item.content).trim() : '';
      if (!text) return;
      this.chatInput = text;
      this.savedPromptDraft = text;
      this.setActivePrompt(item.name || '提示词库', 'library', text);
      this.promptConfigVisible = false;
      this.$Message.info(`已填入提示词「${(item && item.name) || ''}」`);
    },
    openSavePromptModal() {
      const content = (this.chatInput || '').trim();
      if (!content) {
        this.$Message.warning('请先输入要保存的提示词内容');
        return;
      }
      this.savePromptForm = { name: '', content };
      this.savePromptModalVisible = true;
    },
    async submitSavePrompt() {
      const name = (this.savePromptForm.name || '').trim();
      const content = (this.savePromptForm.content || '').trim();
      if (!name || !content) {
        this.$Message.warning('标题和内容不可为空');
        return;
      }
      this.savePromptLoading = true;
      try {
        await this.$api.post('ai/parse', {
          name,
          content,
          mod: PROMPT_MOD,
        });
        this.$Message.success('提示词已保存');
        this.savePromptModalVisible = false;
        this.setActivePrompt(name, 'custom', content);
        await this.fetchCustomPrompts();
        const panel = this.$refs.promptPanel;
        if (panel && panel.fetchPrompts) panel.fetchPrompts();
      } catch (err) {
        this.$Message.error((err && err.message) || '保存失败');
      } finally {
        this.savePromptLoading = false;
      }
    },
    clearChat() {
      this.chatMessages = [];
      this.chatInput = '';
      this.revokeChatImagePreviews();
      this.chatImages = [];
    },
    toggleThinking(msg) {
      if (!msg || msg.streaming) return;
      this.$set(msg, 'thinkingCollapsed', !msg.thinkingCollapsed);
    },
    displayAssistantContent(msg) {
      if (!msg) return '';
      const raw = msg.content || '';
      if (msg.codeApplied) return stripHtmlBlocksForDisplay(raw);
      return raw;
    },
    triggerChatImageInput() {
      if (this.$refs.chatImageInput) this.$refs.chatImageInput.click();
    },
    addChatImageFiles(files) {
      (files || []).forEach((file) => {
        if (!file || !file.type || file.type.indexOf('image/') !== 0) {
          if (file && file.name) this.$Message.warning(`跳过非图片：${file.name}`);
          return;
        }
        this.chatImages.push({
          file,
          name: file.name || `粘贴图片_${Date.now()}`,
          preview: URL.createObjectURL(file),
          url: '',
        });
      });
    },
    onChatImageChange(e) {
      const files = Array.from((e.target && e.target.files) || []);
      if (e.target) e.target.value = '';
      this.addChatImageFiles(files);
    },
    onChatPaste(e) {
      const clipboard = e.clipboardData || (window && window.clipboardData);
      if (!clipboard) return;
      const items = clipboard.items ? Array.from(clipboard.items) : [];
      const imageFiles = [];
      items.forEach((item) => {
        if (item && item.type && item.type.indexOf('image/') === 0) {
          const file = item.getAsFile && item.getAsFile();
          if (file) imageFiles.push(file);
        }
      });
      if (!imageFiles.length && clipboard.files && clipboard.files.length) {
        Array.from(clipboard.files).forEach((f) => {
          if (f.type && f.type.indexOf('image/') === 0) imageFiles.push(f);
        });
      }
      if (!imageFiles.length) return;
      e.preventDefault();
      this.addChatImageFiles(imageFiles);
      this.$Message.success(`已粘贴 ${imageFiles.length} 张图片`);
    },
    removeChatImage(index) {
      const img = this.chatImages[index];
      if (img && img.preview && String(img.preview).startsWith('blob:')) {
        try { URL.revokeObjectURL(img.preview); } catch (e) { /* ignore */ }
      }
      this.chatImages.splice(index, 1);
    },
    async uploadChatImages(images) {
      const list = images || [];
      if (!list.length) return [];
      const needUpload = list.filter(img => img.file && !img.url);
      if (!needUpload.length) {
        return list.map(img => img.url).filter(Boolean);
      }
      const files = needUpload.map(img => img.file);
      const uploaded = await this.$store.dispatch('session/upload', files);
      const urls = (uploaded || []).map(f => (f && f.url) || '').filter(Boolean);
      if (urls.length !== needUpload.length) {
        throw new Error('图片上传未完成');
      }
      needUpload.forEach((img, i) => {
        img.url = urls[i];
      });
      return list.map(img => img.url).filter(Boolean);
    },
    onChatKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendChatMessage();
      }
    },
    stopChatStream() {
      this.chatAborted = true;
      if (this._chatReader && typeof this._chatReader.cancel === 'function') {
        try { this._chatReader.cancel(); } catch (e) { /* ignore */ }
      }
      this._chatReader = null;
      if (this._chatAbortController) {
        try { this._chatAbortController.abort(); } catch (e) { /* ignore */ }
        this._chatAbortController = null;
      }
      const last = this.chatMessages[this.chatMessages.length - 1];
      if (last && last.streaming) {
        last.streaming = false;
        if (!last.content) last.content = '（已停止）';
        this.$set(last, 'thinkingCollapsed', true);
      }
      this.streamingMessage = false;
      this.chatSending = false;
    },
    async sendChatMessage(options = {}) {
      const skipClearInput = !!(options && options.skipClearInput);
      const text = (this.chatInput || '').trim();
      const pendingImages = this.chatImages.slice();
      if ((!text && !pendingImages.length) || this.chatSending) return;

      this.chatAborted = false;
      this.chatStickToBottom = true;
      this.chatSending = true;
      this.streamingMessage = false;

      let imageUrls = [];
      try {
        if (pendingImages.length) {
          imageUrls = await this.uploadChatImages(pendingImages);
        }
      } catch (err) {
        this.chatSending = false;
        this.$Message.error((err && err.message) || '图片上传失败');
        return;
      }

      const userMsg = {
        role: 'user',
        content: text || (imageUrls.length ? '请根据图片生成/修改单文件 HTML 页面' : ''),
        images: imageUrls.map((url, i) => ({
          url,
          name: (pendingImages[i] && pendingImages[i].name) || `图片${i + 1}`,
        })),
      };
      this.chatMessages.push(userMsg);
      if (!skipClearInput) {
        this.chatInput = '';
        this.revokeChatImagePreviews();
        this.chatImages = [];
      }
      this.chatStickToBottom = true;
      this.$nextTick(() => this.scrollChatToBottom(true));

      const streamDone = (errMsg) => {
        if (this.chatAborted && !errMsg) {
          this.streamingMessage = false;
          this.chatSending = false;
          return;
        }
        const last = this.chatMessages[this.chatMessages.length - 1];
        if (last && last.streaming) {
          last.streaming = false;
          if (errMsg) last.content = errMsg;
          if (!errMsg) this.$set(last, 'thinkingCollapsed', true);
        }
        this.streamingMessage = false;
        this.chatSending = false;
        this._chatReader = null;
        this._chatAbortController = null;
        this.$nextTick(() => {
          this.scrollChatToBottom();
          if (!errMsg && !this.chatAborted && last && last.role === 'assistant' && last.content) {
            this.extractCodeToContent(last);
          }
        });
      };

      this.devStreamChat(streamDone).catch((e) => {
        if (this.chatAborted || (e && e.name === 'AbortError')) {
          streamDone();
          return;
        }
        const errMsg = (e && e.message) || '请求失败';
        const last = this.chatMessages[this.chatMessages.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          last.content = errMsg;
          streamDone(errMsg);
        } else {
          this.chatMessages.push({ role: 'assistant', content: errMsg });
          streamDone(errMsg);
        }
      });
    },
    regenerateFromMessage() {
      if (this.chatSending) return;
      this.chatInput = '请重新生成。小改用 ```miniapp-edit 行级补丁；大改或新建请输出完整 HTML（含 <!DOCTYPE html>）。第三方库请使用国内 CDN。';
      this.sendChatMessage();
    },
    buildApiMessages() {
      const messages = [
        { role: 'system', content: this.systemPrompt },
      ];

      messages.push({
        role: 'system',
        content: buildEditorSourceMessage(this.editorContent),
      });

      const history = this.chatMessages.filter(m => m.role === 'user' || m.role === 'assistant');
      history.forEach((m, idx) => {
        const isLast = idx === history.length - 1;
        if (m.role === 'user') {
          const imgs = (m.images || []).map(x => x.url || x).filter(Boolean);
          if (isLast && imgs.length) {
            const parts = [];
            if (m.content) parts.push({ type: 'text', text: m.content });
            imgs.forEach((url) => {
              parts.push({ type: 'image_url', image_url: { url } });
            });
            messages.push({ role: 'user', content: parts });
          } else {
            messages.push({ role: 'user', content: m.content || '' });
          }
        } else {
          messages.push({
            role: 'assistant',
            content: stripHtmlBlocksForHistory(m.content) || '（已输出 HTML）',
          });
        }
      });

      return messages;
    },
    async devStreamChat(streamDone) {
      const messages = this.buildApiMessages();
      const payload = {
        model_id: DEEPSEEK_MODEL,
        messages,
        stream: true,
        thinking: true,
        value_type: 'text',
      };

      this.chatMessages.push({
        role: 'assistant',
        content: '',
        thinking: '',
        thinkingCollapsed: false,
        streaming: true,
        codeApplied: false,
        extractFailed: false,
        applyMode: '',
      });
      this.streamingMessage = true;
      const streamMsg = this.chatMessages[this.chatMessages.length - 1];

      const applyUpdate = (content, thinking) => {
        const prevContent = streamMsg.content || '';
        if (content != null) this.$set(streamMsg, 'content', content);
        if (thinking != null) this.$set(streamMsg, 'thinking', thinking);
        // 正文开始出现后折叠思考，避免挡住结果底部
        if (content && String(content).trim() && !prevContent.trim()) {
          this.$set(streamMsg, 'thinkingCollapsed', true);
        }
        this.$nextTick(() => {
          this.scrollThinkingToBottom();
          this.scrollChatToBottom();
        });
      };

      // 优先走 AiBot / DeepSeek（$ai.chat → /ai/chat）
      if (this.$ai && typeof this.$ai.chat === 'function') {
        try {
          await readAssistantChatStream(
            () => this.$ai.chat(payload).then((reader) => {
              this._chatReader = reader;
              return reader;
            }),
            (state) => {
              if (this.chatAborted) return;
              applyUpdate(state.content || '', state.reasoning || '');
            },
          );
          if (!streamMsg.content && !streamMsg.thinking) {
            this.$set(streamMsg, 'content', '暂无回复');
          }
          streamDone();
          return;
        } catch (e) {
          if (this.chatAborted || (e && e.name === 'AbortError')) {
            streamDone();
            return;
          }
          throw e;
        }
      }

      await this.devStreamChatFetch(payload, streamMsg, applyUpdate, streamDone);
    },
    async devStreamChatFetch(payload, streamMsg, applyUpdate, streamDone) {
      const url = (this.$api.defaults.baseURL || '').replace(/\/$/, '') + CHAT_API;
      const headers = {
        ...(this.$api.defaults.headers || {}),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream, application/json, text/plain, */*',
      };
      this._chatAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'same-origin',
        signal: this._chatAbortController ? this._chatAbortController.signal : undefined,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.msg || `HTTP ${res.status}`);
      }

      const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
      const isStream = contentType.indexOf('text/event-stream') !== -1
        || contentType.indexOf('stream') !== -1
        || contentType.indexOf('text/plain') !== -1;

      if (!res.body || !isStream) {
        const data = await res.json().catch(() => ({}));
        const reply = (data.data && data.data.content) || data.content || data.message || '';
        const thinking = (data.data && data.data.reasoning_content)
          || data.reasoning_content
          || data.thinking
          || '';
        applyUpdate(reply || '暂无回复', thinking || '');
        streamDone();
        return;
      }

      const reader = res.body.getReader();
      this._chatReader = {
        cancel: () => reader.cancel(),
      };
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let fullThinking = '';
      const isPlainText = contentType.indexOf('text/plain') !== -1;

      try {
        while (true) {
          if (this.chatAborted) break;
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          if (isPlainText) {
            fullContent += chunk;
            applyUpdate(fullContent, fullThinking);
            continue;
          }

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (!line.startsWith('data:')) continue;
            const raw = line.replace(/^data:\s*/, '').trim();
            if (raw === '[DONE]' || !raw) continue;
            try {
              const data = JSON.parse(raw);
              const parts = extractStreamParts(data);
              if (parts.content) fullContent += parts.content;
              if (parts.reasoning) fullThinking += parts.reasoning;
              if (parts.content || parts.reasoning) applyUpdate(fullContent, fullThinking);
            } catch (_) { /* ignore partial json */ }
          }
        }

        if (!this.chatAborted) {
          if (isPlainText && buffer) {
            fullContent += buffer;
          } else if (buffer.trim()) {
            const last = buffer.trim();
            if (last.startsWith('data:')) {
              const raw = last.replace(/^data:\s*/, '').trim();
              if (raw && raw !== '[DONE]') {
                try {
                  const data = JSON.parse(raw);
                  const parts = extractStreamParts(data);
                  if (parts.content) fullContent += parts.content;
                  if (parts.reasoning) fullThinking += parts.reasoning;
                } catch (_) { /* ignore */ }
              }
            }
          }
          if (!fullContent.trim() && !fullThinking.trim()) {
            fullContent = '暂无回复';
          }
          applyUpdate(fullContent, fullThinking);
        }
      } finally {
        streamDone();
      }
    },
    extractCodeToContent(msgOrText) {
      const msg = typeof msgOrText === 'string'
        ? { content: msgOrText }
        : msgOrText;
      const assistantText = msg && msg.content;
      const applied = applyAssistantCodeToSource(this.editorContent, assistantText);
      if (!applied.ok) {
        if (msg && typeof msg === 'object') {
          this.$set(msg, 'codeApplied', false);
          this.$set(msg, 'extractFailed', true);
          this.$set(msg, 'extractError', applied.error || '未能提取可应用的代码');
          this.$set(msg, 'applyMode', '');
        }
        this.$Message.warning(applied.error || '未能提取可应用的代码');
        return false;
      }
      const prevLen = String(this.editorContent || '').length;
      const nextLen = String(applied.html || '').length;
      // 整页替换时若明显变短，多半是提取截断，拒绝覆盖
      if (
        applied.mode === 'full'
        && prevLen > 2000
        && nextLen > 0
        && nextLen < prevLen * 0.5
      ) {
        if (msg && typeof msg === 'object') {
          this.$set(msg, 'codeApplied', false);
          this.$set(msg, 'extractFailed', true);
          this.$set(msg, 'extractError', `提取结果过短（${nextLen}/${prevLen} 字符），疑似截断，未覆盖左侧代码`);
        }
        this.$Message.warning(`提取结果过短（${nextLen}/${prevLen}），已拒绝覆盖，请重新生成`);
        return false;
      }
      this.editorContent = applied.html;
      this.lastCodeAppliedAt = Date.now();
      this.viewMode = 'preview';
      this.$nextTick(() => this.updatePreviewUrl());
      if (msg && typeof msg === 'object') {
        this.$set(msg, 'codeApplied', true);
        this.$set(msg, 'extractFailed', false);
        this.$set(msg, 'extractError', '');
        this.$set(msg, 'extractedHtml', applied.html);
        this.$set(msg, 'applyMode', applied.mode || 'full');
      }
      const tip = applied.mode === 'patch'
        ? `已应用补丁（${applied.opsCount || 0} 处）并更新左侧`
        : `已提取完整 HTML（${nextLen} 字符）并更新左侧`;
      this.$Message.success(tip);
      return true;
    },
    reapplyExtractedCode(msg) {
      if (msg && msg.extractedHtml) {
        this.editorContent = msg.extractedHtml;
        this.lastCodeAppliedAt = Date.now();
        this.viewMode = 'preview';
        this.$nextTick(() => this.updatePreviewUrl());
        this.$Message.success('已重新应用到左侧');
        return;
      }
      const applied = applyAssistantCodeToSource(this.editorContent, msg && msg.content);
      if (!applied.ok) {
        this.$Message.warning(applied.error || '未找到可应用的代码');
        return;
      }
      this.editorContent = applied.html;
      this.lastCodeAppliedAt = Date.now();
      this.viewMode = 'preview';
      this.$nextTick(() => this.updatePreviewUrl());
      this.$Message.success('已重新应用到左侧');
    },
    onChatMessagesScroll(e) {
      if (Date.now() < (this._chatScrollIgnoreUntil || 0)) return;
      const el = e && e.target;
      if (!el) return;
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.chatStickToBottom = distance <= 48;
    },
    scrollThinkingToBottom() {
      let body = this.$refs.streamingThinkingBody;
      if (Array.isArray(body)) body = body[0];
      if (body) body.scrollTop = body.scrollHeight;
    },
    scrollChatToBottom(force) {
      if (!force && !this.chatStickToBottom) return;
      this.$nextTick(() => {
        const wrap = this.$refs.chatMessagesWrap;
        if (!wrap) return;
        this._chatScrollIgnoreUntil = Date.now() + 120;
        wrap.scrollTop = wrap.scrollHeight;
        // markdown 渲染后再补一次，避免滚不到真正底部
        requestAnimationFrame(() => {
          if (!force && !this.chatStickToBottom) return;
          wrap.scrollTop = wrap.scrollHeight;
        });
      });
    },
    async saveContent() {
      if (!this.editorEditId) return;
      this.contentSaving = true;
      try {
        const skills = this.getEditorSkillsIds();
        await this.$api.put(`${API_BASE}/${this.editorEditId}`, {
          content: this.editorContent,
          skills,
        });
        const row = this.items.find(r => r.id === this.editorEditId);
        if (row) {
          row.content = this.editorContent;
          row.skills = skills;
        }
        this.markEditorSaved();
        this.$Message.success('保存成功');
        this._forceCloseEditor = true;
        this.doCloseEditor();
        this._forceCloseEditor = false;
      } catch (err) {
        this.$Message.error(err.message || '保存失败');
      } finally {
        this.contentSaving = false;
      }
    },
    openMetaEdit(row) {
      this.metaModalTitle = '修改应用';
      this.metaEditId = row.id;
      this.metaFormData = {
        name: row.name || '',
        icon: row.icon || '',
        content: row.content || '',
        description: row.description || '',
        private: normalizePrivate(row.private),
        disabled: row.disabled === 1 ? 1 : 0,
      };
      this.metaModalVisible = true;
    },
    async handleMetaSubmit() {
      try {
        await this.$refs.metaFormRef.validate();
        this.metaConfirmLoading = true;
        const payload = {
          name: this.metaFormData.name,
          icon: this.metaFormData.icon || undefined,
          description: this.metaFormData.description || undefined,
          private: normalizePrivate(this.metaFormData.private),
        };
        if (this.metaEditId) {
          payload.content = this.metaFormData.content;
          payload.disabled = this.metaFormData.disabled;
          await this.$api.put(`${API_BASE}/${this.metaEditId}`, payload);
          this.$Message.success('修改成功');
        } else {
          payload.content = this.metaFormData.content || '';
          await this.$api.post(API_BASE, payload);
          this.$Message.success('新建成功');
        }
        this.metaModalVisible = false;
        this.fetchData();
      } catch (err) {
        if (err && err.message) this.$Message.error(err.message);
      } finally {
        this.metaConfirmLoading = false;
      }
    },
    async toggleDisabled(row, value) {
      const disabled = value === 1 ? 1 : 0;
      try {
        await this.$api.put(`${API_BASE}/${row.id}`, { disabled });
        row.disabled = disabled;
        this.$Message.success(disabled ? '已禁用' : '已启用');
      } catch (err) {
        this.$Message.error(err.message || '操作失败');
      }
    },
    handleDelete(row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除应用「${row.name}」吗？`,
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${row.id}`);
            this.$Message.success('删除成功');
            this.fetchData();
          } catch (err) {
            this.$Message.error(err.message || '删除失败');
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.miniapps-page.full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(180deg, #f7f9fc 0%, #fff 120px);
}
.page-toolbar {
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.92);
}
.search-bar .flex-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.search-input {
  width: 280px;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 15px;
  display: flex;
  flex-direction: column;
}
.table-wrap >>> .gz-table,
.table-wrap >>> .ivu-table-wrapper {
  height: 100%;
  flex: 1;
  min-height: 0;
}
.table-wrap >>> .ivu-table-body {
  overflow: auto;
}
.icon-clip-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.icon-clip-wrap .l-gz-image {
  width: 80px;
  height: 80px;
  min-width: 80px;
  min-height: 80px;
}
.icon-hint {
  font-size: 12px;
  color: var(--subtext2);
}
.form-hint {
  margin-left: 8px;
  font-size: 12px;
  color: var(--subtext2);
}

.miniapp-editor-layout {
  display: flex;
  height: 78vh;
  gap: 16px;
}
.editor-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.editor-left-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  flex-shrink: 0;
}
.editor-left-toolbar-left,
.editor-left-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.code-applied-tip {
  font-size: 12px;
  color: #059669;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  padding: 2px 10px;
}
.preview-size-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--border);
  background: #fafbfc;
  flex-shrink: 0;
}
.preview-size-label {
  font-size: 12px;
  color: var(--subtext2);
  margin-right: 2px;
}
.size-chip {
  border-radius: 999px !important;
}
.editor-left-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}
.preview-viewport {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}
.preview-iframe-wrap {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}
.preview-iframe {
  display: block;
  border: none;
  background: #fff;
}
.preview-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subtext2);
  font-size: 13px;
  padding: 16px;
  text-align: center;
}
.status-text {
  font-size: 12px;
  color: var(--subtext2);
}
.code-textarea {
  width: 100%;
  min-height: 400px;
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  flex: 1;
}
.code-textarea >>> textarea {
  min-height: 420px;
  font-family: Consolas, Monaco, monospace;
  border-radius: 10px;
}

.editor-right {
  width: 440px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.editor-right-top.chat-panel {
  flex: 1;
  min-height: 0;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.chat-panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
  color: #fff;
}
.chat-panel-title-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.chat-panel-title {
  font-weight: 600;
  font-size: 14px;
}
.chat-panel-sub {
  font-size: 11px;
  opacity: 0.72;
}
.chat-panel-actions >>> .chat-clear-btn.ivu-btn {
  color: #e2e8f0 !important;
  border: 1px solid rgba(226, 232, 240, 0.55) !important;
  background: rgba(255, 255, 255, 0.08) !important;
}
.chat-panel-actions >>> .chat-clear-btn.ivu-btn:hover {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.85) !important;
  background: rgba(255, 255, 255, 0.16) !important;
}
.chat-panel-actions >>> .chat-clear-btn.ivu-btn[disabled] {
  color: rgba(226, 232, 240, 0.55) !important;
  border-color: rgba(226, 232, 240, 0.25) !important;
  background: rgba(255, 255, 255, 0.04) !important;
  opacity: 1;
}
.panel-placeholder {
  font-size: 12px;
  color: var(--subtext2);
  padding: 8px 0;
}

.prompt-config-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background: #f8fafc;
}
.prompt-active {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  font-size: 12px;
  color: #334155;
}
.prompt-active-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.prompt-config-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.prompt-icon-btn {
  color: #0f172a !important;
  border-color: #94a3b8 !important;
  background: #fff !important;
  font-weight: 600;
}
.prompt-icon-btn >>> .ivu-icon {
  color: #0f172a !important;
  font-size: 16px;
  font-weight: 700;
}
.prompt-icon-btn[disabled],
.prompt-icon-btn.ivu-btn[disabled] {
  color: #94a3b8 !important;
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}
.prompt-icon-btn[disabled] >>> .ivu-icon {
  color: #94a3b8 !important;
}
.prompt-config-panel {
  max-height: 420px;
  overflow-y: auto;
  padding: 4px 2px;
}
.prompt-config-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #0f172a;
}
.prompt-config-divider {
  margin: 10px 0 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}
.prompt-config-empty {
  font-size: 12px;
  color: var(--subtext2);
  margin-bottom: 8px;
}
.preset-group {
  margin-bottom: 8px;
}
.preset-group-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}
.preset-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.preset-chip {
  border: 1px solid var(--border);
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 1.6;
  cursor: pointer;
  transition: all 0.15s ease;
}
.preset-chip:hover:not(:disabled) {
  border-color: var(--primary);
  color: var(--primary);
  background: #eff6ff;
}
.preset-chip.active {
  border-color: var(--primary);
  background: var(--primary);
  color: #fff;
}
.preset-chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.preset-saved {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed var(--border);
}
.preset-saved >>> .ai-prompt-toolbar-outer {
  margin-bottom: 4px;
}
.preset-saved >>> .ai-prompt-editor {
  display: none;
}

.chat-messages-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}
.chat-empty-hint {
  font-size: 12px;
  color: var(--subtext2);
  line-height: 1.7;
  padding: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px dashed var(--border);
  border-radius: 10px;
}
.dev-chat-msg {
  margin-bottom: 14px;
}
.dev-chat-msg:last-child {
  margin-bottom: 0;
}
.dev-chat-msg.msg-user .msg-bubble {
  margin-left: 20px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #fff;
  border-radius: 14px 14px 4px 14px;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.22);
}
.dev-chat-msg.msg-bot .msg-bubble {
  margin-right: 12px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px 14px 14px 4px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}
.msg-bubble {
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}
.msg-bubble.bot.typing {
  color: var(--subtext2);
  font-style: italic;
}
.msg-user-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.msg-user-image {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: #fff;
}
.msg-user-text {
  white-space: pre-wrap;
}
.msg-markdown-wrap {
  max-width: 100%;
}
.msg-markdown-wrap >>> .markdown-body {
  font-size: 13px;
  line-height: 1.55;
}
.msg-thinking {
  margin-bottom: 10px;
  padding: 8px 10px;
  background: linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%);
  border: 1px solid #bfdbfe;
  border-radius: 8px;
}
.msg-thinking-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  user-select: none;
}
.msg-thinking-live {
  font-weight: 400;
  color: #64748b;
}
.msg-thinking-caret {
  margin-left: auto;
}
.msg-thinking-body {
  margin-top: 6px;
  max-height: 120px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.msg-thinking-body pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.55;
  color: #475569;
  font-family: inherit;
}
.msg-code-applied {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #0f766e;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 8px;
}
.msg-code-applied a {
  margin-left: auto;
  color: var(--primary);
}
.msg-extract-failed {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
}
.msg-extract-failed .ivu-btn {
  margin-left: auto;
}
.cursor-blink::after {
  content: '|';
  animation: blink 0.8s step-end infinite;
}
@keyframes blink {
  50% { opacity: 0; }
}

.chat-composer {
  flex-shrink: 0;
  padding: 10px 12px 12px;
  border-top: 1px solid var(--border);
  background: #fff;
}
.chat-images-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.chat-image-thumb {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: #fff;
}
.chat-image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.chat-image-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.chat-input-area {
  width: 100%;
}
.chat-composer >>> textarea {
  border-radius: 10px;
  resize: none;
}
.chat-input-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.chat-input-actions-left,
.chat-input-actions-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chat-send-btn {
  min-width: 64px;
}
.chat-stop-btn {
  color: #b91c1c !important;
  border: 1px solid #f87171 !important;
  background: #fef2f2 !important;
  font-weight: 600;
}
.chat-stop-btn >>> .ivu-icon {
  color: #b91c1c !important;
}
.chat-stop-btn:hover {
  color: #991b1b !important;
  border-color: #ef4444 !important;
  background: #fee2e2 !important;
}

.skills-pop-panel {
  max-height: 280px;
  overflow-y: auto;
}
.skills-pop-title {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}
.skills-check-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.skill-check-item {
  display: flex !important;
  align-items: flex-start;
  white-space: normal;
  line-height: 1.4;
}
.skill-check-item .skill-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}
.skill-check-item .skill-desc {
  display: block;
  font-size: 12px;
  color: var(--subtext2);
  margin-top: 2px;
}
</style>

<style>
.miniapp-editor-modal .ivu-modal {
  top: 24px;
}
.miniapp-editor-modal .ivu-modal-body {
  padding: 12px 16px 16px;
}
.login-tag {
  display: inline-block;
  min-width: 64px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 500;
}
.login-tag--yes {
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fcd34d;
}
.login-tag--no {
  color: #047857;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}
.miniapp-prompt-config-pop .ivu-poptip-body-content,
.miniapp-skills-pop .ivu-poptip-body-content {
  white-space: normal;
}
.prompt-config-actions .prompt-icon-btn.ivu-btn {
  color: #0f172a !important;
  border-color: #64748b !important;
  background: #fff !important;
}
.prompt-config-actions .prompt-icon-btn.ivu-btn .ivu-icon {
  color: #0f172a !important;
}
.chat-input-actions-right .chat-stop-btn.ivu-btn {
  color: #b91c1c !important;
  border-color: #f87171 !important;
  background: #fef2f2 !important;
}
.chat-input-actions-right .chat-stop-btn.ivu-btn .ivu-icon {
  color: #b91c1c !important;
}
</style>
